import { useCallback, useState } from 'react';
import { FAST_API_BASE_PEOPLE } from '../config';
import { exceedsWordLimit } from '../utils/text';
import { ANALYTICS_EVENTS, trackEvent } from '../analytics';

// Messages for the backend's companies_status after "Add Companies & Save". "added" needs
// no message because the new companies appear in the list.
export const COMPANIES_NOTICES = {
  failed: { status: 'warning', message: "Saved, but companies couldn't be added." },
  none_found: { status: 'info', message: 'Saved. No new companies were found in your summary or experience.' }
};

const readErrorMessage = (body, fallback) => {
  if (typeof body?.detail === 'string') {
    return body.detail;
  }
  if (Array.isArray(body?.detail) && body.detail[0]?.msg) {
    return body.detail[0].msg;
  }
  return fallback;
};

// Saves the edited profile. Saves with long summary/experience text are held back
// behind a prompt so the user can choose to summarise first or save anyway.
export const useProfileSave = ({
  person,
  accessToken,
  resolvedPersonId,
  buildPayload,
  editedDescription,
  editedExperience,
  setIsSaving,
  setSaveError,
  setIsEditing,
  refreshPerson,
  onSummarise
}) => {
  // Options for the save waiting on the long-text prompt; null when the prompt is closed.
  const [pendingSaveOptions, setPendingSaveOptions] = useState(null);
  // Message about the outcome of "Add Companies & Save", shown after the save completes.
  const [companiesNotice, setCompaniesNotice] = useState(null);

  const performSave = useCallback(async ({ addCompanies = false } = {}) => {
    if (!person || !accessToken || !resolvedPersonId) {
      return;
    }

    const payload = addCompanies ? { ...buildPayload(), add_companies: true } : buildPayload();

    setIsSaving(true);
    setSaveError(null);
    setCompaniesNotice(null);

    try {
      const response = await fetch(`${FAST_API_BASE_PEOPLE}/${resolvedPersonId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`
        },
        body: JSON.stringify(payload)
      });

      let parsedBody = null;
      try {
        parsedBody = await response.json();
      } catch (_) {
        parsedBody = null;
      }

      if (!response.ok) {
        throw new Error(readErrorMessage(parsedBody, 'Failed to save changes'));
      }

      if (addCompanies) {
        setCompaniesNotice(COMPANIES_NOTICES[parsedBody?.companies_status] ?? null);
      }
      trackEvent(addCompanies ? ANALYTICS_EVENTS.PROFILE_SAVED_WITH_COMPANIES : ANALYTICS_EVENTS.PROFILE_SAVED);
      refreshPerson();
      setIsEditing(false);
    } catch (err) {
      setSaveError(err.message || 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  }, [person, accessToken, resolvedPersonId, buildPayload, setIsSaving, setSaveError, refreshPerson, setIsEditing]);

  // Entry point for save buttons. Ignores non-option arguments such as click events.
  const requestSave = useCallback(
    (options) => {
      const saveOptions = { addCompanies: options?.addCompanies === true };
      if (exceedsWordLimit(editedDescription) || exceedsWordLimit(editedExperience)) {
        setPendingSaveOptions(saveOptions);
        return;
      }
      performSave(saveOptions);
    },
    [editedDescription, editedExperience, performSave]
  );

  const confirmSaveAnyway = useCallback(() => {
    const saveOptions = pendingSaveOptions;
    setPendingSaveOptions(null);
    if (saveOptions) {
      trackEvent(ANALYTICS_EVENTS.LONG_TEXT_SAVE_ANYWAY);
      performSave(saveOptions);
    }
  }, [pendingSaveOptions, performSave]);

  const chooseSummarise = useCallback(() => {
    setPendingSaveOptions(null);
    trackEvent(ANALYTICS_EVENTS.LONG_TEXT_SUMMARISE);
    onSummarise();
  }, [onSummarise]);

  const cancelLongText = useCallback(() => {
    setPendingSaveOptions(null);
  }, []);

  const clearCompaniesNotice = useCallback(() => {
    setCompaniesNotice(null);
  }, []);

  return {
    requestSave,
    companiesNotice,
    clearCompaniesNotice,
    isLongTextPromptOpen: pendingSaveOptions !== null,
    confirmSaveAnyway,
    chooseSummarise,
    cancelLongText
  };
};

export default useProfileSave;
